import { useId, useReducer, useRef, useState } from 'react';
import { Pencil, RefreshCw, RotateCcw, Utensils } from 'lucide-react';

import { DIET_FOODS, DIET_NUMBERS, DIET_WEEK, IMPORT_PAPER, PLATE_MEALS, SHOW_PLAN_IMPORT, TODAY_INDEX, formatNumber, useLanding } from './landingData';
import { dietStep, foodOf, startDiet, swapOptions, totalsOf } from './dietDemo';
import { Pill, SectionHead } from './parts';

const DAY_SHORT_KEYS = ['monShort', 'tueShort', 'wedShort', 'thuShort', 'friShort', 'satShort', 'sunShort'];
// The plate's day as the plan view draws it today: breakfast logged, lunch next (the meal the demo changes), the rest later
const PLATE_STATES = ['done', 'next', 'later', 'later'];
const LUNCH = 1;
const MACROS = ['protein', 'carbs', 'fat'];

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

// "Batata, doce, cozida" as the food search writes it: the first word bold, the rest after it
const DbName = ({ name }) => {
  const [head, ...rest] = name.split(', ');
  return (
    <>
      <b>{head}</b>
      {rest.length > 0 && `, ${rest.join(', ')}`}
    </>
  );
};

// The plate: one arc per meal sized by its calories, 1.2% gaps, butt caps (DESIGN.md › Plano alimentar)
const Plate = ({ meals, total, language, unit }) => {
  const size = 124;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const gap = circumference * 0.012;
  let at = 0;
  const arcs = meals.map((meal) => {
    const length = (circumference * meal.kcal) / total - gap;
    const arc = (
      <circle
        key={meal.key}
        className={`lp-plate__arc is-${meal.state}`}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeDasharray={`${length} ${circumference - length}`}
        strokeDashoffset={-at}
      />
    );
    at += length + gap;
    return arc;
  });
  return (
    <span className="lp-plate__ring lp-diet__plate" aria-hidden="true">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} focusable="false">
        {arcs}
      </svg>
      <span className="lp-plate__in">
        <b>{formatNumber(total, language)}</b>
        <span>{unit}</span>
      </span>
    </span>
  );
};

/**
 * Plano alimentar (Pro), drawn from the app's B · Agora. The week worked out for you: the numbers it comes from, each
 * day's calories and today's plate. Then the plan changed by hand, which works here: "Trocar refeição" brings another
 * lunch with the same calories and near-identical macros, "Desfazer troca" brings the last one back, and a food opens
 * beside it to be swapped for a database food weighed to the same calories. Prepared data, no AI. Under it, while
 * SHOW_PLAN_IMPORT is on, bringing a nutritionist's plan in.
 */
export const HomeDiet = ({ showImport = SHOW_PLAN_IMPORT }) => {
  const { t, language, name } = useLanding();
  const d = (key, options) => t(`pages.Home.diet.${key}`, options);
  const ui = (key, options) => t(`pages.DietPlan.ui.${key}`, options);
  const fmt = (value) => formatNumber(value, language);
  const kcal = (value) => `${fmt(value)} kcal`;

  const [state, dispatch] = useReducer(dietStep, undefined, startDiet);
  const [selected, setSelected] = useState(0);
  const [picked, setPicked] = useState(null);
  // What settles in after a change: the whole list (a meal swapped or undone) or one row (a food swapped)
  const [settle, setSettle] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  const swapRef = useRef(null);
  const sheetTitleRef = useRef(null);
  const rowRefs = useRef([]);
  const ids = useId();
  const sheetId = `${ids}-sheet`;
  const titleId = `${ids}-title`;
  const hintId = `${ids}-hint`;

  const foods = state.foods.map((entry) => foodOf(entry, language));
  const totals = totalsOf(foods);
  const lunchName = name(PLATE_MEALS[LUNCH]);
  const quantity = (food) => (food.spoons ? d('spoon', { count: food.spoons, grams: food.grams }) : `${fmt(food.grams)} g`);

  // The day adds up from its meals, lunch as it is now
  const dayMeals = PLATE_MEALS.map((meal, index) => ({ key: meal.en, label: name(meal), kcal: index === LUNCH ? totals.kcal : meal.calories, state: PLATE_STATES[index] }));
  const dayTotal = dayMeals.reduce((total, meal) => total + meal.kcal, 0);
  const dayName = t('pages.DietPlan.thursday');

  // The food open beside the meal, and what could take its place
  const at = Math.min(selected, foods.length - 1);
  const current = foods[at];
  const options = swapOptions(state.foods[at], state.foods).map((entry) => foodOf(entry, language));
  const preview = options.find((option) => option.id === picked) || null;
  const mealWithPreview = preview ? totals.kcal - current.kcal + preview.kcal : totals.kcal;

  const after = (focus) => requestAnimationFrame(() => focus()?.focus());

  const swapMeal = () => {
    const next = dietStep(state, { type: 'swapMeal' }).foods.map((entry) => foodOf(entry, language));
    dispatch({ type: 'swapMeal' });
    setPicked(null);
    setSettle('list');
    setListVersion((version) => version + 1);
    setAnnouncement(d('announceMeal', { foods: new Intl.ListFormat(language, { type: 'conjunction' }).format(next.map((food) => lowerFirst(food.name))), kcal: fmt(totalsOf(next).kcal) }));
  };

  const undo = () => {
    dispatch({ type: 'undo' });
    setPicked(null);
    setSettle('list');
    setListVersion((version) => version + 1);
    setAnnouncement(d('announceUndo'));
    // The undo gives way to the swap: the focus goes back to it
    after(() => swapRef.current);
  };

  const openFood = (index) => {
    setSelected(index);
    setPicked(null);
    after(() => sheetTitleRef.current);
  };

  const swapFood = () => {
    if (!preview) return;
    dispatch({ type: 'swapFood', index: at, entry: { id: preview.id, grams: preview.grams, fromDb: true } });
    setPicked(null);
    setSettle(at);
    setAnnouncement(d('announceFood', { food: preview.name, grams: fmt(preview.grams), meal: lunchName, kcal: fmt(mealWithPreview) }));
    // Back to the meal, on the food that came in (as the app's sheet closes onto its row)
    after(() => rowRefs.current[at]);
  };

  return (
    <section id="dieta" className="lp-sec" aria-labelledby="lp-diet-title">
      <div className="lp-wrap">
        <SectionHead id="lp-diet-title" title={d('title')} sub={d('sub')} pill={<Pill kind="pro">{t('pages.Home.build.pro')}</Pill>} />
        <p className="lp-lead lp-lead--more">{d('custom')}</p>

        <div className="lp-diet" role="group" aria-label={d('demoLabel')} aria-describedby={hintId}>
          <p id={hintId} className="sr-only">
            {d('demoHint')}
          </p>

          <div className="lp-diet__week-card">
            <p className="lp-cal__when">
              {d('week')} <Pill kind="neutral">{t('pages.Home.demo.example')}</Pill>
            </p>
            <dl className="lp-diet__calc">
              {[
                ['bmr', DIET_NUMBERS.bmr],
                ['tdee', DIET_NUMBERS.tdee],
                ['target', DIET_NUMBERS.target],
              ].map(([key, value]) => (
                <div key={key} className={key === 'target' ? 'is-goal' : undefined}>
                  <dt>
                    <b>{d(key)}</b>
                    <span>{d(`${key}For`)}</span>
                  </dt>
                  <dd>
                    {fmt(value)} <small>kcal</small>
                  </dd>
                </div>
              ))}
            </dl>
            <ul className="lp-diet__week" aria-label={d('weekLabel')}>
              {DIET_WEEK.map((value, index) => (
                <li key={DAY_SHORT_KEYS[index]} className={index === TODAY_INDEX ? 'is-on' : undefined} aria-current={index === TODAY_INDEX ? 'date' : undefined}>
                  <span className="lp-diet__dpill">{t(`pages.DietPlan.${DAY_SHORT_KEYS[index]}`)}</span>
                  <span className="lp-diet__dkcal">{fmt(value ?? dayTotal)}</span>
                </li>
              ))}
            </ul>
            <div className="lp-plate lp-diet__day">
              <Plate meals={dayMeals} total={dayTotal} language={language} unit={ui('platePlanned')} />
              <ul aria-label={d('dayMeals', { day: language === 'pt' ? dayName.toLowerCase() : dayName })}>
                {dayMeals.map((meal) => (
                  <li key={meal.key}>
                    <i className={`is-${meal.state}`} />
                    {meal.label}
                    <span>{fmt(meal.kcal)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lp-diet__meal">
            <div className="lp-cal__next-head">
              <span className="lp-cal__disc" aria-hidden="true">
                <Utensils />
              </span>
              <span>
                <span className="lp-cal__next-title">
                  {lunchName} <Pill>{ui('nextTag')}</Pill>
                </span>
                <span className="lp-cal__next-meta">{ui('foodsCount', { count: foods.length })}</span>
              </span>
            </div>
            <dl className="lp-diet__facts">
              <div>
                <dt>kcal</dt>
                <dd>{fmt(totals.kcal)}</dd>
              </div>
              {MACROS.map((key) => (
                <div key={key}>
                  <dt>{t(`pages.DietPlan.${key}`)}</dt>
                  <dd>{`${fmt(totals[key])} g`}</dd>
                </div>
              ))}
            </dl>
            <ul key={listVersion} className={`lp-diet__foods${settle === 'list' ? ' is-settling' : ''}`}>
              {foods.map((food, index) => (
                <li key={`${index}-${food.id}-${food.grams}`} className={settle === index ? 'is-settling' : undefined}>
                  <button
                    type="button"
                    ref={(node) => {
                      rowRefs.current[index] = node;
                    }}
                    className={`lp-diet__food${index === at ? ' is-on' : ''}`}
                    aria-label={`${ui('editFood', { food: food.name, quantity: quantity(food) })}, ${kcal(food.kcal)}`}
                    aria-controls={sheetId}
                    aria-current={index === at ? 'true' : undefined}
                    onClick={() => openFood(index)}
                  >
                    <span className="lp-diet__fname">
                      {food.name}{' '}
                      <small>
                        {quantity(food)}
                        <Pencil aria-hidden="true" />
                      </small>
                    </span>
                    <b className="lp-diet__fkcal">{kcal(food.kcal)}</b>
                    <span className="lp-diet__fmac">
                      {ui('foodMacros', { protein: Math.round(food.protein), carbs: Math.round(food.carbs), fat: Math.round(food.fat) })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="lp-diet__acts">
              <button type="button" ref={swapRef} className="lp-btn lp-btn--outline lp-diet__swap-meal" onClick={swapMeal}>
                <RefreshCw aria-hidden="true" />
                {ui('swapMeal')}
              </button>
              {state.undo ? (
                <button type="button" className="lp-btn lp-diet__undo" onClick={undo}>
                  <RotateCcw aria-hidden="true" />
                  {ui('undoSwap')}
                </button>
              ) : (
                <p className="lp-diet__acts-note">{d('swapNote')}</p>
              )}
            </div>
            <p className="sr-only" aria-live="polite">
              {announcement}
            </p>
          </div>

          <div className="lp-diet__sheet" id={sheetId} role="group" aria-labelledby={titleId}>
            <p id={titleId} ref={sheetTitleRef} className="lp-diet__sheet-title" tabIndex={-1}>
              {current.name}
            </p>
            <p className="lp-diet__sheet-sub">{[lunchName, quantity(current), kcal(current.kcal)].join(' · ')}</p>
            <fieldset className="lp-diet__opts">
              <legend>{d('swapFor')}</legend>
              {options.map((option) => (
                <label key={option.id} className={`lp-diet__opt${picked === option.id ? ' is-on' : ''}`}>
                  <input type="radio" name={`${ids}-food`} value={option.id} checked={picked === option.id} onChange={() => setPicked(option.id)} />
                  <span className="lp-diet__opt-name">
                    <span>
                      <DbName name={option.name} />
                    </span>
                    <small>{d('per100', { kcal: fmt(DIET_FOODS[option.id].per100[0]) })}</small>
                  </span>
                  <span className="lp-diet__opt-amount">
                    <b>{`${fmt(option.grams)} g`}</b>
                    <small>{kcal(option.kcal)}</small>
                  </span>
                </label>
              ))}
            </fieldset>
            <div className="lp-diet__preview">
              {preview ? (
                <>
                  <dl className="lp-diet__facts lp-diet__facts--food">
                    <div>
                      <dt>kcal</dt>
                      <dd>{fmt(preview.kcal)}</dd>
                    </div>
                    {MACROS.map((key) => (
                      <div key={key}>
                        <dt>{t(`pages.DietPlan.${key}`)}</dt>
                        <dd>{`${fmt(Math.round(preview[key]))} g`}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="lp-diet__line">{ui('editMealLine', { meal: lunchName, kcal: fmt(mealWithPreview), planned: fmt(totals.kcal) })}</p>
                </>
              ) : (
                <p className="lp-diet__hint">{d('pickHint', { food: lowerFirst(current.name) })}</p>
              )}
            </div>
            <button type="button" className="lp-btn lp-btn--go lp-diet__swap" onClick={swapFood} disabled={!preview}>
              {ui('editSwap')}
            </button>
          </div>
        </div>

        {showImport && (
          <div className="lp-diet__import">
            <div>
              <h3 className="lp-diet__import-title">{d('importTitle')}</h3>
              <p className="lp-diet__import-body">{d('importBody')}</p>
            </div>
            <div className="lp-paper lp-diet__paper" aria-hidden="true">
              <p className="lp-paper__h">{IMPORT_PAPER[language].title}</p>
              <p className="lp-diet__paper-meal">{IMPORT_PAPER[language].meal}</p>
              <ul>
                {IMPORT_PAPER[language].lines.map((line) => (
                  <li key={line.text}>
                    <span>{line.text}</span>
                    <Pill kind="neutral">{kcal(line.kcal)}</Pill>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
